import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { setDocumentTitle } from '../../../utils/documentTitle';
import './NurseAppLandingPage.css';

const NurseAppLandingPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Feature flag: ซ่อน Coming Soon section ไว้ก่อน (สามารถเปิดได้ในอนาคต)
  // ตั้งค่าเป็น true เมื่อต้องการแสดง Coming Soon section
  const SHOW_COMING_SOON_SECTION = false;

  useEffect(() => {
    // Set document title
    setDocumentTitle('', true); // true = isNurseApp
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/nurse');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="nurse-landing-page">
      {/* Navigation Bar */}
      <nav className={`landing-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-content">
            <div className="nav-logo">
              <img 
                src="/logo.png" 
                alt="NurseApp Logo" 
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (!e.target.nextSibling) {
                    const fallback = document.createElement('span');
                    fallback.className = 'logo-icon';
                    fallback.textContent = '🩺';
                    e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
                  }
                }}
              />
              <span className="logo-text">NurseApp</span>
            </div>
            <div className="nav-actions">
              <Link to="/login" className="nav-link">เข้าสู่ระบบ</Link>
              <button onClick={handleGetStarted} className="btn-primary">
                เริ่มใช้งานฟรี
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text animate-fade-in">
              <h1 className="hero-title">
                แอพสำหรับพยาบาล
                <br />
                <span className="highlight">จัดการเวรและอุปกรณ์ได้ง่ายในที่เดียว</span>
              </h1>
              <p className="hero-subtitle">
                ระบบตรวจสอบอุปกรณ์อัตโนมัติ ลดเวลาและความผิดพลาด
                <br />
                แลกเวรได้ง่าย อนุมัติได้เร็ว
              </p>
              <div className="hero-cta">
                <button onClick={handleGetStarted} className="btn-hero">
                  เริ่มใช้งานฟรี
                </button>
                <p className="hero-install-note">
                  📱 สามารถติดตั้งเป็นแอพได้ทั้ง iOS และ Android
                </p>
              </div>
            </div>
            <div className="hero-image animate-slide-in">
              <div className="hero-mockup">
                <div className="mockup-screen">
                  <div className="screen-content">
                    <div className="screen-header">
                      <div className="screen-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="screen-body">
                      <div className="feature-card-preview">
                        <div className="preview-icon">📅</div>
                        <div className="preview-text">
                          <div className="preview-title">จัดการเวร</div>
                          <div className="preview-desc">ดูตารางเวร • เข้า-ออกเวร</div>
                        </div>
                      </div>
                      <div className="feature-card-preview">
                        <div className="preview-icon">🩺</div>
                        <div className="preview-text">
                          <div className="preview-title">บริหารอุปกรณ์</div>
                          <div className="preview-desc">ตรวจสอบอัตโนมัติ • ยืม-คืน</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">ฟีเจอร์เด่น</h2>
            <p className="section-subtitle">ทุกสิ่งที่คุณต้องการในการทำงาน</p>
          </div>
          <div className="features-grid">
            <div className="feature-card animate-fade-up" style={{ animationDelay: '0.1s' }}>
              <div className="feature-icon">📅</div>
              <h3 className="feature-title">จัดการเวร</h3>
              <p className="feature-description">
                ดูตารางเวรรายเดือน/รายสัปดาห์/รายวัน เข้า-ออกเวรได้ง่าย
                แลกเวรกับเพื่อนร่วมทีม ขออนุมัติลา/เปลี่ยนเวร
              </p>
              <ul className="feature-list">
                <li>✅ ดูตารางเวรได้ทุกที่ทุกเวลา</li>
                <li>✅ เข้า-ออกเวรได้สะดวก</li>
                <li>✅ แลกเวรได้ง่าย</li>
                <li>✅ ขออนุมัติได้เร็ว</li>
              </ul>
            </div>

            <div className="feature-card animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="feature-icon">🩺</div>
              <h3 className="feature-title">บริหารอุปกรณ์</h3>
              <p className="feature-description">
                ตรวจสอบอุปกรณ์อัตโนมัติตามรอบเวลา ติดตาม Stock อัตโนมัติ
                ยืม-คืนอุปกรณ์ได้สะดวก ส่งซ่อมและติดตามสถานะ
              </p>
              <ul className="feature-list">
                <li>✅ ตรวจสอบอัตโนมัติ</li>
                <li>✅ ติดตาม Stock แบบ Real-time</li>
                <li>✅ ยืม-คืนได้ง่าย</li>
                <li>✅ ส่งซ่อมและติดตามได้</li>
              </ul>
            </div>

            <div className="feature-card animate-fade-up" style={{ animationDelay: '0.3s' }}>
              <div className="feature-icon">👥</div>
              <h3 className="feature-title">จัดการทีม</h3>
              <p className="feature-description">
                สร้างทีมและเชิญสมาชิก จัดการสมาชิกในทีม
                ติดตามการทำงานของทีม อนุมัติคำขอเข้าร่วมทีม
              </p>
              <ul className="feature-list">
                <li>✅ สร้างทีมได้ง่าย</li>
                <li>✅ จัดการสมาชิกได้สะดวก</li>
                <li>✅ ติดตามการทำงาน</li>
                <li>✅ อนุมัติคำขอได้เร็ว</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section - ซ่อนไว้ก่อน สามารถเปิดได้โดยเปลี่ยน SHOW_COMING_SOON_SECTION เป็น true */}
      {SHOW_COMING_SOON_SECTION && (
      <section className="coming-soon-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">ฟีเจอร์ใหม่ที่กำลังจะมา</h2>
            <p className="section-subtitle">กำลังพัฒนาเพื่อประสบการณ์ที่ดีขึ้น</p>
          </div>
          <div className="coming-soon-grid">
            <div className="coming-soon-card">
              <div className="coming-soon-badge">เร็วๆ นี้</div>
              <div className="coming-soon-icon">🏆</div>
              <h3 className="coming-soon-title">Gamification</h3>
              <p className="coming-soon-description">
                สะสม XP, Level Up, รับ Badges และ Achievements
                <br />
                ติดตามความก้าวหน้าและแข่งขันกับเพื่อนร่วมทีม
              </p>
              <ul className="coming-soon-features">
                <li>✨ ระบบสะสม XP อัตโนมัติ</li>
                <li>🎖️ Badges และ Achievements</li>
                <li>📊 Leaderboard และ Ranking</li>
                <li>🎯 Challenges รายวัน/รายสัปดาห์</li>
              </ul>
            </div>

            <div className="coming-soon-card">
              <div className="coming-soon-badge">เร็วๆ นี้</div>
              <div className="coming-soon-icon">🎁</div>
              <h3 className="coming-soon-title">ระบบให้ของขวัญ</h3>
              <p className="coming-soon-description">
                ให้ของขวัญเป็นกำลังใจกับเพื่อนร่วมทีม
                <br />
                สะสม T Coins และแลกของรางวัล
              </p>
              <ul className="coming-soon-features">
                <li>💝 ให้ของขวัญเป็นกำลังใจ</li>
                <li>🪙 สะสม T Coins</li>
                <li>🛍️ แลกของรางวัล</li>
                <li>📜 ประวัติการให้/รับของขวัญ</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2 className="section-title">ทำไมต้องเลือก Nurse App?</h2>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">⚡</div>
                  <div className="benefit-content">
                    <h3>ประหยัดเวลา</h3>
                    <p>ลดเวลาในการจัดการเวรและตรวจสอบอุปกรณ์</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✅</div>
                  <div className="benefit-content">
                    <h3>ลดความผิดพลาด</h3>
                    <p>ระบบตรวจสอบอัตโนมัติช่วยลดความผิดพลาด</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">📱</div>
                  <div className="benefit-content">
                    <h3>ใช้งานได้ทุกที่</h3>
                    <p>ใช้งานได้ทั้ง Desktop และ Mobile</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🔒</div>
                  <div className="benefit-content">
                    <h3>ปลอดภัย</h3>
                    <p>ข้อมูลถูกเก็บรักษาอย่างปลอดภัย</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🎮</div>
                  <div className="benefit-content">
                    <h3>เพิ่มความสนุกในการทำงาน</h3>
                    <p>สะสม XP และรับรางวัลจากการทำงานประจำวัน</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="benefits-image">
              <div className="benefits-visual">
                <div className="visual-card logo-card">
                  <img 
                    src="/logo.png" 
                    alt="NurseApp Logo" 
                    className="benefits-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (!e.target.nextSibling) {
                        const fallback = document.createElement('div');
                        fallback.className = 'benefits-logo-fallback';
                        fallback.textContent = '🩺';
                        e.target.parentNode.appendChild(fallback);
                      }
                    }}
                  />
                  <div className="benefits-logo-fallback" style={{ display: 'none' }}>🩺</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">วิธีใช้งาน</h2>
            <p className="section-subtitle">ง่ายๆ เพียง 3 ขั้นตอน</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">ลงทะเบียน</h3>
              <p className="step-description">
                สมัครสมาชิกฟรี ไม่ต้องใช้บัตรเครดิต
              </p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">สร้างหรือเข้าร่วมทีม</h3>
              <p className="step-description">
                สร้างทีมใหม่หรือเข้าร่วมทีมที่มีอยู่
              </p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">เริ่มใช้งาน</h3>
              <p className="step-description">
                จัดการเวรและอุปกรณ์ได้ทันที
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">เสียงจากผู้ใช้</h2>
            <p className="section-subtitle">ผู้ใช้ที่ไว้วางใจเรา</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <div className="star-rating">
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                  </div>
                  <span className="rating-score">5.0</span>
                </div>
                <p className="testimonial-text">
                  "ใช้ NurseApp แล้วจัดการเวรได้ง่ายขึ้นมาก ไม่ต้องใช้กระดาษแล้ว แลกเวรกับเพื่อนร่วมทีมได้ง่าย ระบบแจ้งเตือนช่วยให้ไม่พลาดเวร"
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">👩‍⚕️</div>
                <div className="author-info">
                  <div className="author-name">พยาบาล [แผนก]</div>
                  <div className="author-role">พยาบาล</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <div className="star-rating">
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                  </div>
                  <span className="rating-score">5.0</span>
                </div>
                <p className="testimonial-text">
                  "ตรวจสอบอุปกรณ์ได้สะดวกขึ้นมาก ระบบติดตาม Stock อัตโนมัติช่วยให้รู้สถานะอุปกรณ์ได้ทันที จัดการทีมได้ง่ายขึ้น"
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">👨‍⚕️</div>
                <div className="author-info">
                  <div className="author-name">หัวหน้าทีม [แผนก]</div>
                  <div className="author-role">หัวหน้าทีม</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <div className="star-rating">
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                    <span className="star filled">⭐</span>
                  </div>
                  <span className="rating-score">5.0</span>
                </div>
                <p className="testimonial-text">
                  "NurseApp ช่วยให้การจัดการเวรและอุปกรณ์มีประสิทธิภาพมากขึ้น ลดเวลาและความผิดพลาดในการตรวจสอบอุปกรณ์"
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">👔</div>
                <div className="author-info">
                  <div className="author-name">ผู้บริหาร [โรงพยาบาล]</div>
                  <div className="author-role">ผู้บริหาร</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">คำถามที่พบบ่อย</h2>
            <p className="section-subtitle">คำตอบสำหรับคำถามที่คุณสงสัย</p>
          </div>
          <div className="faq-list">
            <FAQItem
              question="Nurse App ใช้ฟรีหรือไม่?"
              answer="ใช่ Nurse App ใช้ฟรี ไม่มีค่าใช้จ่าย ไม่ต้องใช้บัตรเครดิต"
            />
            <FAQItem
              question="ต้องติดตั้งแอพหรือไม่?"
              answer="ไม่จำเป็น คุณสามารถใช้งานผ่านเว็บเบราว์เซอร์ได้เลย หรือติดตั้งเป็นแอพ (PWA) เพื่อใช้งานได้สะดวกขึ้น - รองรับทั้ง iOS และ Android"
            />
            <FAQItem
              question="รองรับอุปกรณ์อะไรบ้าง?"
              answer="รองรับทั้ง Desktop, Tablet และ Mobile ใช้งานได้บนทุกอุปกรณ์"
            />
            <FAQItem
              question="ข้อมูลปลอดภัยหรือไม่?"
              answer="ใช่ ข้อมูลของคุณถูกเก็บรักษาอย่างปลอดภัยด้วยระบบความปลอดภัยระดับสูง"
            />
            <FAQItem
              question="สามารถใช้ได้หลายทีมหรือไม่?"
              answer="ได้ คุณสามารถเข้าร่วมได้หลายทีม แต่ละทีมจะมีข้อมูลแยกกัน"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">พร้อมเริ่มใช้งาน NurseApp แล้วหรือยัง?</h2>
            <p className="cta-subtitle">
              ลงทะเบียนฟรีและเริ่มจัดการเวรและอุปกรณ์ได้ง่ายขึ้นวันนี้
            </p>
            <button onClick={handleGetStarted} className="btn-cta">
              เริ่มใช้งานฟรี
            </button>
            <p className="cta-install-note">
              📱 ติดตั้งเป็นแอพได้ทั้ง iOS และ Android เพื่อใช้งานได้สะดวกขึ้น
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img 
                  src="/logo.png" 
                  alt="NurseApp Logo" 
                  className="logo-image"
                  style={{ width: '2rem', height: '2rem', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (!e.target.nextSibling) {
                      const fallback = document.createElement('span');
                      fallback.className = 'logo-icon';
                      fallback.textContent = '🩺';
                      e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
                    }
                  }}
                />
                <span className="logo-text">NurseApp</span>
              </div>
              <p className="footer-description">
                แอพสำหรับพยาบาล จัดการเวรและอุปกรณ์ได้ง่ายในที่เดียว
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">ลิงก์</h4>
              <ul className="footer-links">
                <li><Link to="/login">เข้าสู่ระบบ</Link></li>
                <li><Link to="/register">ลงทะเบียน</Link></li>
                <li>
                  <a 
                    href="#features" 
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('features');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    ฟีเจอร์
                  </a>
                </li>
                <li>
                  <a 
                    href="#faq" 
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('faq');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    คำถามที่พบบ่อย
                  </a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">ติดต่อ</h4>
              <ul className="footer-links">
                <li>
                  <a href="mailto:luthirasceo@gmail.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                    Email: luthirasceo@gmail.com
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61584549759313" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    เพจ: NurseApp
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 NurseApp. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{question}</span>
        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

export default NurseAppLandingPage;

